import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      toast.success(t('contact.success'));
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-20 bg-brand-white" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <span className="font-accent text-brand-gold text-sm tracking-[0.3em] uppercase">{t('contact.contactUs')}</span>
          <h1 className="font-heading text-4xl lg:text-5xl font-bold text-brand-black mt-2">{t('contact.title')}</h1>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-8"
          >
            <div>
              <h2 className="font-heading text-3xl text-brand-black mb-4">{t('contact.stayInTouch')}</h2>
              <p className="text-brand-dark/80 leading-relaxed">
                {t('contact.description')}
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-brand-gold/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-6 w-6 text-brand-gold" />
                </div>
                <div>
                  <h3 className="text-brand-black font-semibold mb-1">{t('contact.address')}</h3>
                  <p className="text-brand-dark/80">130 Topham Terrace, Orléans, ON</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-brand-gold/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="h-6 w-6 text-brand-gold" />
                </div>
                <div>
                  <h3 className="text-brand-black font-semibold mb-1">{t('contact.phone')}</h3>
                  <p className="text-brand-dark/80">+1(613)327-0164</p>
                  <p className="text-brand-dark/50 text-sm">Lun - Dim, 9h - 22h</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-brand-gold/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="h-6 w-6 text-brand-gold" />
                </div>
                <div>
                  <h3 className="text-brand-black font-semibold mb-1">{t('contact.email')}</h3>
                  <p className="text-brand-dark/80">alinaelegance1@gmail.com</p>
                </div>
              </div>
            </div>

            {/* Map placeholder */}
            <div className="aspect-video bg-brand-cream rounded-lg flex items-center justify-center">
              <p className="text-brand-dark/50">{t('contact.interactiveMap')}</p>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <form onSubmit={handleSubmit} className="bg-brand-cream border border-brand-gold/20 rounded-lg p-8 space-y-6">
              <h2 className="font-heading text-2xl text-brand-black mb-6">{t('contact.sendMessage')}</h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-brand-dark">{t('contact.name')}</Label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <Label className="text-brand-dark">{t('contact.email')}</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black"
                    data-testid="contact-email"
                  />
                </div>
              </div>

              <div>
                <Label className="text-brand-dark">{t('contact.subject')}</Label>
                <Input
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black"
                  data-testid="contact-subject"
                />
              </div>

              <div>
                <Label className="text-brand-dark">{t('contact.message')}</Label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black resize-none"
                  data-testid="contact-message"
                />
              </div>

              <Button 
                type="submit"
                disabled={loading}
                className="w-full bg-brand-gold text-white hover:bg-brand-gold py-6 font-bold uppercase tracking-wider rounded"
                data-testid="contact-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" /> {t('contact.sending')}
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-5 w-5" /> {t('contact.send')}
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
